// FILE: src/components/case-studies/admin/StageBoard.js
//
// The PRIMARY authoring surface. Stages render as ordered cards; each owns
// its sections, artifacts, and linked questions. Every reorder/move is an
// explicit RPC (admin_reorder_case_questions / admin_reorder_case_stages —
// atomic, two-phased server-side) followed by a reload: pessimistic by
// design, so there is no optimistic state to roll back.

import React, { useState } from 'react';
import {
  adminDeleteCaseArtifact, adminDeleteCaseSection, adminDeleteCaseStage,
  adminReorderCaseQuestions, adminReorderCaseStages, adminUnlinkCaseQuestion,
  adminUpsertCaseStage,
} from '../../../services/caseStudyService';
import {
  computeMovePayload, computeStageMovePayload, computeSwapPayload,
  hasUniqueSequences, stageDeletionBlockers,
} from './caseAdminUtils';
import { SectionModal, ArtifactModal, AttachQuestionModal, QuestionArtifactRefsModal } from './CaseItemModals';
import { S, T } from './caseAdminStyles';

export default function StageBoard({ full, stageModel, onChanged, onEditQuestion }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // {kind, stage, item?}

  async function run(fn) {
    setBusy(true); setError(null);
    try { await fn(); await onChanged(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const caseId = full.case.id;

  function moveQuestion(qid, targetStageId, position) {
    const payload = computeMovePayload(stageModel, qid, targetStageId, position);
    if (!hasUniqueSequences(payload)) { setError('Internal error: duplicate sequences in reorder payload.'); return; }
    run(() => adminReorderCaseQuestions(caseId, payload));
  }

  function swapQuestion(stageId, i, j) {
    const payload = computeSwapPayload(stageModel, stageId, i, j);
    run(() => adminReorderCaseQuestions(caseId, payload));
  }

  function moveStage(index, delta) {
    const payload = computeStageMovePayload(stageModel, index, delta);
    if (payload) run(() => adminReorderCaseStages(caseId, payload));
  }

  function deleteStage(stage) {
    const blockers = stageDeletionBlockers(stage);
    if (blockers.length > 0) { setError(`Cannot delete "${stage.title}": ${blockers.join('; ')}.`); return; }
    const content = stage.sections.length + stage.artifacts.length;
    const msg = content > 0
      ? `Delete stage "${stage.title}" and its ${content} section(s)/artifact(s)?`
      : `Delete empty stage "${stage.title}"?`;
    if (!window.confirm(msg)) return;
    run(() => adminDeleteCaseStage(stage.id));
  }

  const iconBtn = (label, onClick, disabled, testId) => (
    <button style={{ ...S.btnGhost, ...(disabled ? { opacity: 0.35, cursor: 'default' } : {}) }}
      disabled={disabled || busy} onClick={onClick} data-testid={testId} aria-label={testId}>{label}</button>
  );

  return (
    <div data-testid="stage-board">
      {error && <div style={S.err} data-testid="stage-board-error">{error}</div>}
      {busy && <div style={{ ...S.sub, marginBottom: 8 }} data-testid="stage-board-busy">Applying…</div>}

      {stageModel.map((stage, idx) => (
        <div key={stage.id} style={{ ...S.panel, marginBottom: 14 }} data-testid={`stage-card-${stage.sequence}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={S.chip(T.brandSoft, T.brand)}>Stage {stage.sequence}</span>
            <strong style={{ fontSize: '0.95rem' }}>{stage.title}</strong>
            <span style={{ flex: 1 }} />
            {iconBtn('↑', () => moveStage(idx, -1), idx === 0, `stage-up-${stage.sequence}`)}
            {iconBtn('↓', () => moveStage(idx, +1), idx === stageModel.length - 1, `stage-down-${stage.sequence}`)}
            {iconBtn('Rename', () => setModal({ kind: 'stage', stage }), false, `stage-edit-${stage.sequence}`)}
            {iconBtn('Delete', () => deleteStage(stage), false, `stage-delete-${stage.sequence}`)}
          </div>
          {stage.intro && <div style={{ ...S.sub, marginTop: 6 }}>{stage.intro}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 12 }}>
            <Cell title="Narrative sections" testId={`stage-${stage.sequence}-sections`}
              onAdd={() => setModal({ kind: 'section', stage })}>
              {stage.sections.map((s) => (
                <Item key={s.id} title={s.title} meta={s.section_type}
                  onEdit={() => setModal({ kind: 'section', stage, item: s })}
                  onDelete={() => window.confirm(`Delete section "${s.title}"?`) &&
                    run(() => adminDeleteCaseSection(s.id))} />
              ))}
            </Cell>

            <Cell title="Artifacts" testId={`stage-${stage.sequence}-artifacts`}
              onAdd={() => setModal({ kind: 'artifact', stage })}>
              {stage.artifacts.map((a) => (
                <Item key={a.id} title={a.title} meta={a.artifact_type}
                  onEdit={() => setModal({ kind: 'artifact', stage, item: a })}
                  onDelete={() => window.confirm(`Delete artifact "${a.title}"?`) &&
                    run(() => adminDeleteCaseArtifact(a.id))} />
              ))}
            </Cell>

            <Cell title="Questions (content edits live in the Question Editor)"
              testId={`stage-${stage.sequence}-questions`}
              onAdd={() => setModal({ kind: 'attach', stage })} addLabel="+ Attach">
              {stage.questions.map((q, qi) => (
                <div key={q.id} style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: '7px 9px', marginBottom: 6 }}
                  data-testid={`board-question-${q.id}`}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                    <strong style={{ fontSize: '0.78rem' }}>Q{q.case_sequence}</strong>
                    <span style={S.chip(T.line, T.muted)}>{q.type}</span>
                    <span style={{ flex: 1 }} />
                    {iconBtn('↑', () => swapQuestion(stage.id, qi, qi - 1), qi === 0, `q-up-${q.id}`)}
                    {iconBtn('↓', () => swapQuestion(stage.id, qi, qi + 1), qi === stage.questions.length - 1, `q-down-${q.id}`)}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: T.ink, margin: '4px 0' }}>
                    {(q.question || '').slice(0, 110)}{(q.question || '').length > 110 ? '…' : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: '0.74rem' }}
                      value={stage.id} disabled={busy} data-testid={`q-move-${q.id}`}
                      aria-label={`Move ${q.id} to another stage`}
                      onChange={(e) => moveQuestion(q.id, e.target.value)}>
                      {stageModel.map((st2) => (
                        <option key={st2.id} value={st2.id}>Stage {st2.sequence}: {st2.title}</option>
                      ))}
                    </select>
                    {onEditQuestion && iconBtn('✎ Edit', () => onEditQuestion(q.id), false, `q-edit-${q.id}`)}
                    {iconBtn(`Artifacts (${(full.question_artifacts || []).filter((r) => r.question_id === q.id).length})`,
                      () => setModal({ kind: 'refs', stage, item: q }), false, `q-refs-${q.id}`)}
                    {iconBtn('Unlink', () => window.confirm(`Unlink ${q.id} from this case? The question itself is preserved.`) &&
                      run(() => adminUnlinkCaseQuestion(q.id)), false, `q-unlink-${q.id}`)}
                  </div>
                </div>
              ))}
            </Cell>
          </div>
        </div>
      ))}

      <button style={S.btn} data-testid="stage-add-button" disabled={busy}
        onClick={() => run(() => adminUpsertCaseStage({
          case_study_id: caseId,
          sequence: stageModel.length + 1,
          title: `Stage ${stageModel.length + 1}`,
        }))}>
        + Add stage
      </button>

      {modal && modal.kind === 'stage' && (
        <StageRenameModal stage={modal.stage} onClose={() => setModal(null)}
          onSave={(patch) => { setModal(null); run(() => adminUpsertCaseStage({ id: modal.stage.id, ...patch })); }} />
      )}
      {modal && modal.kind === 'section' && (
        <SectionModal caseId={caseId} stage={modal.stage} item={modal.item}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />
      )}
      {modal && modal.kind === 'artifact' && (
        <ArtifactModal caseId={caseId} stage={modal.stage} item={modal.item}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />
      )}
      {modal && modal.kind === 'attach' && (
        <AttachQuestionModal full={full} stage={modal.stage} stageModel={stageModel}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />
      )}
      {modal && modal.kind === 'refs' && (
        <QuestionArtifactRefsModal full={full} question={modal.item}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged(); }} />
      )}
    </div>
  );
}

function Cell({ title, children, onAdd, addLabel = '+ Add', testId }) {
  return (
    <div style={{ background: T.bg, borderRadius: 8, padding: 10 }} data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{title}</span>
        <span style={{ flex: 1 }} />
        <button style={S.btnGhost} onClick={onAdd}>{addLabel}</button>
      </div>
      {children}
    </div>
  );
}

function Item({ title, meta, onEdit, onDelete }) {
  return (
    <div style={{ border: `1px solid ${T.line}`, background: T.panel, borderRadius: 8, padding: '6px 9px', marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        <div style={{ fontSize: '0.68rem', color: T.muted }}>{meta}</div>
      </div>
      <button style={S.btnGhost} onClick={onEdit}>Edit</button>
      <button style={S.btnGhost} onClick={onDelete}>✕</button>
    </div>
  );
}

function StageRenameModal({ stage, onClose, onSave }) {
  const [title, setTitle] = useState(stage.title);
  const [intro, setIntro] = useState(stage.intro || '');
  return (
    <div style={S.modalScrim} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={S.h1}>Edit stage</h2>
        <label style={S.label}>Title</label>
        <input style={S.input} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="stage-title-input" />
        <label style={S.label}>Intro (optional stage lead-in shown on unlock)</label>
        <textarea style={{ ...S.input, minHeight: 70 }} value={intro} onChange={(e) => setIntro(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} data-testid="stage-title-save" onClick={() => onSave({ title, intro })}>Save</button>
        </div>
      </div>
    </div>
  );
}
